import React from 'react';
import LwtTable from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Table';
import useClassrooms from 'state/hooks/use-classrooms';
import useReports from 'state/hooks/use-reports';
import StudentRowMoreOptions from './StudentRowMoreOptions';
import { Button, Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TableColumnProps } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Table';
import {
  FONT_GSS_FUTURA_BOOK,
  FONT_GSS_FUTURA_SEMI_BOLD,
} from '@monorepo/shared/src/themes/defaultTheme';
import {
  remCalc,
  translate,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import UseAssignmentStore from 'graph/state/query-hooks/use-assignments-store';
import {
  getStudentGroup,
  getAttemptFiles,
  getProgressDataForStudentAndLetter,
} from '../../utils/reports-util';
import { ActivityTypes } from '@monorepo/shared/src/types';
import FileViewer, {
  fileViewerModalStyles,
} from '@monorepo/shared/src/components/common/FileViewer';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';

const t = (key: string, options?) => translate('reports', key, options);

const ClassroomStudentsTable: React.FC = () => {
  const classes = useStyles();
  const [{ activeClassroom }] = useClassrooms();
  const [{ filteredStudents, filters, progressData, selectedCharacter }] = useReports();
  const [{ assignments }] = UseAssignmentStore();
  const [, { openModal, closeModal }] = useModal();

  const tableData = filteredStudents.map((student) => {
    const group = getStudentGroup(activeClassroom, student.id);
    const studentProgress = progressData?.find((pd) => pd.student?.id === student.id);
    const characterProgress = studentProgress?.characterProgress?.find(
      (cp) => cp.characterId === selectedCharacter?.id
    );

    return {
      id: student.id,
      name: `${student.firstName} ${student.lastName}`,
      group: group?.name,
      bookReadCount: characterProgress?.bookReadCount ?? 0,
      activityCompletedCount: characterProgress?.activityCompletedCount ?? 0,
      activityTotalCount: characterProgress?.activityTotalCount ?? 0,
      activitySkippedCount: characterProgress?.activitySkippedCount ?? 0,
    };
  });

  const viewFilesModal = (files) => {
    openModal({
      ...fileViewerModalStyles,
      Title: () => <BaseModalTitle text={t('turnedInWork')} onClose={closeModal} />,
      body: (
        <Paper className={classes.modal}>
          <FileViewer files={files} fitToViewport />
        </Paper>
      ),
    });
  };

  const FileViewLink = ({ files }) => {
    return (
      <Button onClick={() => viewFilesModal(files)} className={classes.buttonText}>
        {t('numOfFiles', {
          fileCount: files.length,
        })}
      </Button>
    );
  };

  const columns: TableColumnProps<any>[] = [
    {
      label: t('studentName'),
      options: { width: '20%', sortable: true, sortKey: 'name' },
      getValue: (item) => item.name,
    },
    {
      label: t('group'),
      options: { width: '10%', sortable: true, sortKey: 'group' },
      getValue: (item) => item.group,
    },
    {
      label: t('readLetterBook'),
      options: { width: '15%', sortable: true, sortKey: 'bookReadCount' },
      getValue: (item) => t('readLetterBookSummary', { bookReadCount: item.bookReadCount }),
    },
    {
      label: t('activitiesCompleted'),
      options: { width: '15%', sortable: true, sortKey: 'activityCompletedCount' },
      getValue: (item) =>
        t('activitiesCompletedSummary', {
          completedCount: item.activityCompletedCount,
          totalCount: item.activityTotalCount,
        }),
    },
    {
      label: t('activitiesSkipped'),
      options: { width: '15%', sortable: true, sortKey: 'activitySkippedCount' },
      getValue: (item) =>
        t('activitiesSkippedSummary', {
          skippedCount: item.activitySkippedCount,
        }),
    },
    {
      label: t('turnedInWork'),
      options: { width: '15%', sortable: false, sortKey: 'turnedInWork' },
      getValue: (item) => {
        const files = getAttemptFiles({
          assignments,
          studentId: item.id,
          char: selectedCharacter?.character,
          activityType: ActivityTypes.PRACTICE_BOOK,
        });
        return !files?.length ? t('noFiles') : <FileViewLink files={files} />;
      },
    },
    {
      label: '',
      options: { width: '5%', hideOnPrint: true },
      getValue: (item) => {
        const progress = getProgressDataForStudentAndLetter(
          progressData,
          item?.id,
          selectedCharacter?.id
        );

        return (
          <StudentRowMoreOptions
            student={item}
            character={filters.character}
            areAllAssignmentsCompleted={
              progress?.activityCompletedCount === progress?.activityTotalCount
            }
            areAllAssignmentsSkipped={
              progress?.activitySkippedCount >= progress?.activityTotalCount
            }
            noAssignments={!progress}
          />
        );
      },
    },
  ];

  return (
    <LwtTable
      columns={columns}
      data={tableData}
      options={{
        sortable: true,
      }}
      classes={{
        sortIconColor: () => classes.sortIconColor,
        tableContainer: () => classes.tableContainer,
        tableHead: () => classes.tableHead,
        tableBody: () => classes.tableBody,
        tableBodyRow: () => classes.tableRow,
        tableHeadCell: () => classes.tableHeadCell,
      }}
    />
  );
};

const useStyles = makeStyles((theme: any) => ({
  tableContainer: {
    boxShadow: '0px 1px 0px 0px rgba(0, 0, 0, 0.12)',
  },
  tableHead: {
    '& tr': {
      '& th': {
        paddingLeft: 0,
        paddingTop: 0,
        backgroundColor: '#F4F4F4',
      },
    },
  },
  tableHeadCell: {
    '& span': {
      fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
      fontSize: remCalc(16),
      lineHeight: remCalc(24),
    },
  },
  tableBody: {
    borderRight: `1px solid ${theme.palette.divider}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  tableRow: {
    '& td': {
      boxShadow: '-2px 0px 3px 0px rgba(0, 0, 0, 0.1)',
      height: remCalc(48),
      padding: `${remCalc(0)} ${remCalc(12)}`,
      fontSize: remCalc(16),
      lineHeight: remCalc(24),
      fontWeight: 400,
      fontFamily: FONT_GSS_FUTURA_BOOK,
    },
  },
  sortIconColor: {
    color: theme.palette.black.light,
  },
  buttonText: {
    height: remCalc(48),
    fontSize: remCalc(16),
    lineHeight: remCalc(24),
    fontWeight: 400,
    fontFamily: FONT_GSS_FUTURA_BOOK,
    paddingLeft: 0,
    color: theme.palette.link.main,
  },
  modal: {
    background: theme.palette.background.paper,
    marginTop: remCalc(-10),
    height: 'max-content',
    minWidth: remCalc(400),
  },
}));

export default ClassroomStudentsTable;
